export interface ExportShotPayload {
  start: number;
  duration: number;
  title: string;
  subtitle: string;
  color: string;
  modelAssetId?: string;
}

export interface WebExportModel {
  assetId: string;
  name: string;
  dataUrl?: string;
  file?: string;
}

export interface WebExportPayload {
  title: string;
  width: number;
  height: number;
  fps: number;
  shots: ExportShotPayload[];
  models: WebExportModel[];
}

export function buildStandaloneHtml(payload: WebExportPayload): string {
  const data = JSON.stringify(payload);
  const css = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { height: 100%; background: #050507; overflow: hidden; font-family: Inter, system-ui, sans-serif; }
    #stage { position: relative; width: 100vw; height: 100vh; }
    #canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
    .overlay { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 18px; pointer-events: none; text-align: center; padding: 0 8vw; }
    .overlay h1 { color: #f4f4f8; font-weight: 700; letter-spacing: 0.06em; text-shadow: 0 2px 24px rgba(0,0,0,0.7); }
    .overlay p { color: rgba(244,244,248,0.78); text-shadow: 0 1px 12px rgba(0,0,0,0.7); }
    #title { font-size: clamp(28px, 5.5vw, 72px); opacity: 0; transform: translateY(14px); transition: opacity 0.9s ease, transform 0.9s ease; }
    #subtitle { font-size: clamp(14px, 2vw, 26px); opacity: 0; transform: translateY(10px); transition: opacity 0.7s ease, transform 0.7s ease; }
    .controls { position: fixed; left: 50%; bottom: 22px; transform: translateX(-50%); display: flex; align-items: center; gap: 14px; background: rgba(10,10,15,0.82); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px; padding: 10px 16px; backdrop-filter: blur(8px); z-index: 10; }
    .controls button { background: #6366f1; color: #fff; border: none; border-radius: 8px; padding: 8px 18px; font: inherit; cursor: pointer; }
    .controls button:hover { background: #4f46e5; }
    .controls input[type=range] { width: min(42vw, 420px); accent-color: #6366f1; }
    .controls span { color: #c9c9d6; font-variant-numeric: tabular-nums; font-size: 13px; min-width: 96px; text-align: center; }
    .badge { position: fixed; top: 14px; right: 16px; color: rgba(255,255,255,0.4); font-size: 11px; letter-spacing: 0.08em; z-index: 10; }
  `;
  const script = `
    import * as THREE from 'three';
    import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';

    (function () {
      var data = ${data};
      var shots = data.shots;
      var width = data.width;
      var height = data.height;
      var fps = data.fps;

      var canvas = document.getElementById('canvas');
      var titleEl = document.getElementById('title');
      var subtitleEl = document.getElementById('subtitle');
      var timeEl = document.getElementById('time');
      var seekEl = document.getElementById('seek');
      var playBtn = document.getElementById('play');

      var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(window.innerWidth, window.innerHeight);

      var scene = new THREE.Scene();
      var camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 100);

      var modelCache = {};
      var activeModel = null;
      var modelSpinner = null;

      function fitModel(model) {
        var box = new THREE.Box3().setFromObject(model);
        var size = box.getSize(new THREE.Vector3());
        var center = box.getCenter(new THREE.Vector3());
        var max = Math.max(size.x, size.y, size.z) || 1;
        var scale = 2.4 / max;
        model.scale.setScalar(scale);
        model.position.sub(center.clone().multiplyScalar(scale));
        model.position.y += (size.y * scale) / 2 - 0.5;
      }

      function loadModel(assetId) {
        if (activeModel) {
          scene.remove(activeModel);
          activeModel = null;
          modelSpinner = null;
        }
        var model = modelCache[assetId];
        if (!model || !model.loaded) return;
        var clone = model.object.clone(true);
        fitModel(clone);
        scene.add(clone);
        activeModel = clone;
        modelSpinner = clone;
      }

      function base64ToArrayBuffer(base64) {
        var binary = atob(base64.split(',')[1] || base64);
        var bytes = new Uint8Array(binary.length);
        for (var i = 0; i < binary.length; i++) { bytes[i] = binary.charCodeAt(i); }
        return bytes.buffer;
      }

      function loadModelBytes(model) {
        if (model.file) {
          return fetch(model.file).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.arrayBuffer();
          });
        }
        return Promise.resolve(base64ToArrayBuffer(model.dataUrl || ''));
      }

      function prepareModels() {
        var loader = new GLTFLoader();
        data.models.forEach(function (model) {
          loadModelBytes(model).then(function (buffer) {
            loader.parse(buffer, '', function (gltf) {
              modelCache[model.assetId] = { object: gltf.scene, loaded: true };
            }, function (err) {
              console.warn('failed to parse model', model.assetId, err);
            });
          }, function (err) {
            console.warn('failed to load model', model.assetId, err);
          });
        });
      }

      var duration = 0;
      for (var i = 0; i < shots.length; i++) { duration = Math.max(duration, shots[i].start + shots[i].duration); }

      function activeShot(t) {
        for (var i = shots.length - 1; i >= 0; i--) {
          var s = shots[i];
          if (t >= s.start && t < s.start + s.duration) return s;
        }
        return shots[0] || null;
      }

      function hexToRgb(hex) {
        var v = hex.replace('#', '');
        return { r: parseInt(v.slice(0, 2), 16) / 255, g: parseInt(v.slice(2, 4), 16) / 255, b: parseInt(v.slice(4, 6), 16) / 255 };
      }

      function makeBackground(color, seed, size) {
        var c = document.createElement('canvas');
        c.width = size; c.height = Math.round(size * height / width);
        var ctx = c.getContext('2d');
        var rgb = hexToRgb(color);
        var grad = ctx.createRadialGradient(size / 2, size * 0.42, 0, size / 2, size * 0.42, size * 0.75);
        grad.addColorStop(0, 'rgba(' + Math.round(rgb.r * 255 + 30) + ',' + Math.round(rgb.g * 255 + 30) + ',' + Math.round(rgb.b * 255 + 30) + ',1)');
        grad.addColorStop(0.6, color);
        grad.addColorStop(1, '#050507');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, c.width, c.height);
        for (var i = 0; i < 40; i++) {
          var x = ((seed * 7919 + i * 97) % 1000) / 1000 * c.width;
          var y = ((seed * 104729 + i * 53) % 1000) / 1000 * c.height;
          var r = 1 + ((seed + i * 31) % 40) / 8;
          ctx.fillStyle = 'rgba(255,255,255,' + (0.02 + (i % 5) * 0.006) + ')';
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
        var texture = new THREE.CanvasTexture(c);
        texture.colorSpace = THREE.SRGBColorSpace;
        return texture;
      }

      var background = null;
      var currentTexture = null;

      function applyShot(shot, t) {
        if (shot.title !== titleEl.getAttribute('data-title')) {
          titleEl.setAttribute('data-title', shot.title);
          titleEl.textContent = shot.title;
          subtitleEl.textContent = shot.subtitle || '';
          titleEl.style.opacity = 0;
          subtitleEl.style.opacity = 0;
          titleEl.style.transform = 'translateY(14px)';
          subtitleEl.style.transform = 'translateY(10px)';
          requestAnimationFrame(function () {
            titleEl.style.opacity = 1;
            subtitleEl.style.opacity = shot.subtitle ? 1 : 0;
            titleEl.style.transform = 'translateY(0)';
            subtitleEl.style.transform = 'translateY(0)';
          });
        }
        var seed = Math.abs(hashString(shot.title));
        var key = shot.color + ':' + seed % 100;
        if (key !== currentTexture) {
          currentTexture = key;
          if (background) { scene.remove(background); background.material.dispose(); }
          var tex = makeBackground(shot.color, seed, 1024);
          background = new THREE.Mesh(
            new THREE.PlaneGeometry(60, 60 * height / width),
            new THREE.MeshBasicMaterial({ map: tex, fog: false })
          );
          background.position.z = -10;
          scene.add(background);
        }
      }

      function hashString(text) {
        var hash = 2166136261;
        for (var i = 0; i < text.length; i++) { hash ^= text.charCodeAt(i); hash = Math.imul(hash, 16777619); }
        return hash >>> 0;
      }

      function cameraPose(movement, localT) {
        var x = 0, y = 0, z = 5;
        if (movement === 'orbit') { x = Math.sin(localT * Math.PI * 2) * 2.2; y = 0.4; }
        else if (movement === 'handheld') { x = Math.sin(localT * 80) * 0.25; y = Math.cos(localT * 65) * 0.18; z = 4.8 + Math.sin(localT * 120) * 0.15; }
        else if (movement === 'slow_push') { z = 5 - localT * 1.8; }
        else if (movement === 'slow_pull') { z = 3.4 + localT * 1.8; }
        else if (movement === 'dolly' || movement === 'truck') { x = -1.6 + localT * 3.2; }
        else if (movement === 'pan' || movement === 'tilt') { x = Math.sin(localT * Math.PI) * 1.4; y = Math.sin(localT * Math.PI) * 0.8; }
        else if (movement === 'crane') { y = -0.8 + localT * 1.6; z = 5.5 - localT * 0.6; }
        else if (movement === 'reveal') { y = 1.4 - localT * 1.4; z = 6 - localT * 1; }
        return { x: x, y: y, z: z };
      }

      function renderAt(t) {
        var shot = activeShot(t);
        if (!shot) return;
        applyShot(shot, t);
        if (shot.modelAssetId) loadModel(shot.modelAssetId);
        else if (activeModel) { scene.remove(activeModel); activeModel = null; modelSpinner = null; }
        var localT = Math.min(1, Math.max(0, (t - shot.start) / shot.duration));
        var ease = localT < 0.5 ? 4 * localT * localT * localT : 1 - Math.pow(-2 * localT + 2, 3) / 2;
        var pose = cameraPose(shot.cameraMovement || 'static', ease);
        camera.position.set(pose.x, pose.y, pose.z);
        camera.lookAt(0, 0, 0);
        renderer.render(scene, camera);
      }

      var playing = true;
      var current = 0;
      var last = performance.now();

      function tick(now) {
        var dt = Math.min(0.1, (now - last) / 1000);
        last = now;
        if (modelSpinner) { modelSpinner.rotation.y += dt * 0.5; }
        if (playing) {
          current += dt;
          if (current >= duration) {
            if (document.body.dataset.loop === '1') { current = 0; }
            else { current = duration; playing = false; playBtn.textContent = 'Play'; }
          }
          seekEl.value = current;
        }
        timeEl.textContent = fmt(current) + ' / ' + fmt(duration);
        renderAt(current);
        requestAnimationFrame(tick);
      }

      function fmt(s) {
        var m = Math.floor(s / 60);
        var r = s - m * 60;
        return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r.toFixed(1);
      }

      playBtn.addEventListener('click', function () {
        playing = !playing;
        playBtn.textContent = playing ? 'Pause' : 'Play';
        if (playing && current >= duration) current = 0;
      });
      seekEl.max = duration;
      seekEl.addEventListener('input', function () {
        current = Number(seekEl.value);
        renderAt(current);
      });
      seekEl.addEventListener('change', function () { renderAt(current); });

      window.addEventListener('resize', function () {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      });
      document.addEventListener('keydown', function (e) {
        if (e.code === 'Space') { e.preventDefault(); playBtn.click(); }
        if (e.key === 'ArrowRight') { current = Math.min(duration, current + 1 / fps); renderAt(current); }
        if (e.key === 'ArrowLeft') { current = Math.max(0, current - 1 / fps); renderAt(current); }
      });

      renderAt(0);
      prepareModels();
      requestAnimationFrame(tick);
    })();
  `;
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>${escapeHtml(payload.title)} — Cinematic Engine</title>
<style>${css}</style>
</head>
<body data-loop="1">
  <div id="stage">
    <canvas id="canvas"></canvas>
    <div class="overlay">
      <h1 id="title"></h1>
      <p id="subtitle"></p>
    </div>
    <div class="badge">CINEMATIC ENGINE · TREE.JS</div>
  </div>
  <div class="controls">
    <button id="play" type="button">Pause</button>
    <input id="seek" type="range" min="0" value="0" step="0.01"/>
    <span id="time">00:00.0 / 00:00.0</span>
  </div>
  <script type="importmap">
    {
      "imports": {
        "three": "https://unpkg.com/three@0.160.0/build/three.module.js",
        "three/addons/": "https://unpkg.com/three@0.160.0/examples/jsm/"
      }
    }
  </script>
  <script type="module">${script}</script>
</body>
</html>
`;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function downloadHtml(html: string, fileName: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function previewHtml(html: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  window.open(URL.createObjectURL(blob), '_blank');
}

export function safeExportFileName(name: string): string {
  const extMatch = name.match(/\.([a-z0-9]+)$/i);
  const ext = extMatch ? `.${extMatch[1].toLowerCase()}` : '';
  const stem = ext ? name.slice(0, -ext.length) : name;
  const sanitized = stem.replace(/[^a-z0-9_-]+/gi, '-').replace(/(^-|-$)/g, '');
  return `${sanitized || 'model'}${ext}`;
}

export interface FolderManifestEntry {
  text?: string;
  dataUrl?: string;
}

export function buildFolderManifest(
  payload: WebExportPayload
): Record<string, FolderManifestEntry> {
  const manifest: Record<string, FolderManifestEntry> = {};
  const folderPayload: WebExportPayload = {
    ...payload,
    models: payload.models.map((model) => ({
      assetId: model.assetId,
      name: model.name,
      file: `assets/${safeExportFileName(model.name)}`,
    })),
  };
  manifest['index.html'] = { text: buildStandaloneHtml(folderPayload) };
  manifest['project.json'] = { text: JSON.stringify(folderPayload, null, 2) };
  for (const model of payload.models) {
    if (model.dataUrl) {
      manifest[`assets/${safeExportFileName(model.name)}`] = { dataUrl: model.dataUrl };
    }
  }
  return manifest;
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, payload] = dataUrl.split(',');
  const mime = meta.match(/^data:([^;]+)/)?.[1] ?? 'application/octet-stream';
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export interface FolderExportResult {
  ok: boolean;
  cancelled: boolean;
  written: number;
}

export async function exportProjectFolder(payload: WebExportPayload): Promise<FolderExportResult> {
  const picker = (
    window as unknown as {
      showDirectoryPicker?: (options?: {
        id?: string;
        mode?: string;
      }) => Promise<FileSystemDirectoryHandle>;
    }
  ).showDirectoryPicker;
  if (typeof picker !== 'function') {
    throw new Error(
      'File System Access API não suportada neste navegador (Chrome/Edge recomendados)'
    );
  }

  let root: FileSystemDirectoryHandle;
  try {
    root = await picker.call(window, { id: 'cinematic-export', mode: 'readwrite' });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return { ok: false, cancelled: true, written: 0 };
    }
    throw error;
  }

  const manifest = buildFolderManifest(payload);
  let written = 0;
  for (const [path, entry] of Object.entries(manifest)) {
    const segments = path.split('/');
    let handle: FileSystemDirectoryHandle = root;
    for (const segment of segments.slice(0, -1)) {
      handle = await handle.getDirectoryHandle(segment, { create: true });
    }
    const fileHandle = await handle.getFileHandle(segments[segments.length - 1], { create: true });
    const writable = await fileHandle.createWritable();
    if (entry.text !== undefined) {
      await writable.write(entry.text);
    } else if (entry.dataUrl !== undefined) {
      await writable.write(dataUrlToBlob(entry.dataUrl));
    }
    await writable.close();
    written++;
  }
  return { ok: true, cancelled: false, written };
}
